import React, {
  useCallback, useState, useRef, useEffect,
} from 'react';
import { Box } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';

import {
  ContentContainer,
  FlexContainer,
  HeroImage,
  TextSection,
  Title,
  Subtitle,
  SectionTitle,
  ResendBox,
} from './MagicLinkSent.styles';

import { validateEmail } from '@/utils/stringValidators';

import Button from '@/components/Button';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { Header } from '@/pages/Onboarding/components/Header/Header';

const RESEND_COOLDOWN_MS = 15_000;

export interface MagicLinkSentProps {
  /** Hero image src (e.g. LimbotHappy for Spark, SleepioThumbsUp for Sleepio) */
  imageSrc: string;
  imageAlt: string;
  title: string;
  description: string;
  cantFindTitle: string;
  cantFindDescription: string;
  /** Pre-filled email address */
  email: string;
  /** Called when user clicks Resend / Continue with a (possibly edited) email */
  onResend: (email: string) => Promise<{ success: boolean }>;
  buttonLabel: string;
  successMessage?: string;
  errorMessage?: string;
}

const MagicLinkSent: React.FC<MagicLinkSentProps> = ({
  imageSrc,
  imageAlt,
  title,
  description,
  cantFindTitle,
  cantFindDescription,
  email: initialEmail,
  onResend,
  buttonLabel,
  successMessage,
  errorMessage: errorMessageProp,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const resendCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultErrorMessage = t('coverage.pre_onboarding.generic.something_went_wrong');

  const startResendCooldown = useCallback(() => {
    setIsResendDisabled(true);
    if (resendCooldownTimerRef.current) {
      clearTimeout(resendCooldownTimerRef.current);
    }
    resendCooldownTimerRef.current = setTimeout(() => {
      setIsResendDisabled(false);
    }, RESEND_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    startResendCooldown();
    return () => {
      if (resendCooldownTimerRef.current) {
        clearTimeout(resendCooldownTimerRef.current);
        resendCooldownTimerRef.current = null;
      }
    };
  }, [startResendCooldown]);

  const handleResend = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed || !validateEmail(trimmed)) {
      setShowError(true);
      setShowSuccess(false);
      return;
    }

    startResendCooldown();
    setIsSubmitting(true);
    try {
      const res = await onResend(trimmed);
      if (res.success) {
        setShowSuccess(true);
        setShowError(false);
      } else {
        setShowSuccess(false);
        setShowError(true);
      }
    } catch {
      setShowSuccess(false);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, onResend, startResendCooldown]);

  const resendDisabled = isResendDisabled || isSubmitting || !email.trim() || !validateEmail(email.trim());

  return (
    <ContentContainer>
      <Header />
      <FlexContainer maxWidth="xs">
        <HeroImage src={imageSrc} alt={imageAlt} />

        <TextSection>
          <Title variant="h2">{title}</Title>
          <Subtitle>{description}</Subtitle>
          <SectionTitle variant="h3">{cantFindTitle}</SectionTitle>
          <Subtitle>{cantFindDescription}</Subtitle>
        </TextSection>

        <Box display="flex" justifyContent="center" width="100%">
          <ResendBox>
            <TextField
              data-testid="resend-email-input"
              label={t('coverage.pre_onboarding.generic.email_label')}
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setShowSuccess(false);
                setShowError(false);
              }}
              variant="outlined"
              fullWidth
              size="small"
            />
            {showSuccess && (
              <Box mt={1.5}>
                <Alert severity="success">
                  {successMessage || description}
                </Alert>
              </Box>
            )}
            {showError && (
              <Box mt={1.5}>
                <Alert severity="error">
                  {errorMessageProp || defaultErrorMessage}
                </Alert>
              </Box>
            )}
            <Box mt={1.5} />
            <Button
              variant="contained"
              data-testid="resend-email-button"
              type="button"
              fullWidth
              color="primary"
              disabled={resendDisabled}
              rounded
              onClick={handleResend}
              loading={isSubmitting}
              style={{ borderRadius: 100 }}
            >
              {buttonLabel}
            </Button>
          </ResendBox>
        </Box>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

export default MagicLinkSent;
